// src/api/features/modules/types.ts

/**
 * Modules — shared client-side types + query keys
 *
 * IMPORTANT:
 * - Frontend does NOT deal with isActive / status flags anymore.
 * - Frontend does NOT care about audit fields (createdBy/At, updatedBy/At, deletedBy/At).
 * - Backend is responsible for only returning active / non-deleted modules.
 */

export type Module = {
  id: number;
  name: string;

  // Hierarchy
  parentId?: number | null;
  /** Optional denormalized parent info if your API returns it */
  parent?: { id: number; name: string } | null;
};

export namespace ModuleDTO {
  /** Create payload */
  export type Create = {
    name: string;
    parentId?: number | null;
  };

  /** Update payload (partial) */
  export type Update = Partial<Create>;

  /** List/query params (UI-facing) */
  export type ListParams = {
    page?: number;   // default 1
    limit?: number;  // default 10
    q?: string;
    // No isActive / includeDeleted — backend handles that.
  };

  /** Generic paginated response */
  export type Page<T> = {
    items: T[];
    page: number;
    pageSize: number;
    total: number;
  };

  /** Bulk helpers */
  export type BulkIds = { ids: number[] };
}

/* Concrete aliases used around the UI */
export type CreateModuleBody = ModuleDTO.Create;
export type UpdateModuleBody = ModuleDTO.Update;
export type ListModulesQuery = ModuleDTO.ListParams;
export type PaginatedModules = ModuleDTO.Page<Module>;
export type BulkIds = ModuleDTO.BulkIds;

/* =========================
 * React Query Keys
 * ========================= */
export const ModulesKeys = {
  all: ["modules"] as const,
  list: (params?: ModuleDTO.ListParams) =>
    [...ModulesKeys.all, "list", params ?? {}] as const,
  detail: (id: number) => [...ModulesKeys.all, "detail", id] as const,
};
